import { Box, IconButton, Menu } from '@material-ui/core';
import {
  CaptureStatus,
  formatDateTimeReadable,
  formatMoney,
  OrderStatus,
  PayoutStatus,
  RefundStatus,
} from '@nemuru-eng/js-api-client';
import { useState } from 'react';

import { OrderAction } from '@/components/modules/OrderActivity/types/OrderAction';
import { OrderActionType } from '@/components/modules/OrderActivity/types/OrderActionType';
import { CustomDivider } from '@/components/shared/components/CustomDivider';
import { CustomPaper } from '@/components/shared/components/CustomPaper';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { CaptureStatusTag } from '@/components/shared/components/StatusTag/Tags/CaptureStatusTag';
import { PayoutStatusTag } from '@/components/shared/components/StatusTag/Tags/PayoutStatusTag';
import { RefundStatusTag } from '@/components/shared/components/StatusTag/Tags/RefundStatusTag';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface OrderActionDetailProps {
  action: OrderAction;
}

export const OrderActionDetail = ({ action }: OrderActionDetailProps) => {
  const { palette } = useCustomTheme();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  return (
    <>
      <IconButton
        size="small"
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
        }}
      >
        <Icon size={18} color={palette.grey[300]}>
          info
        </Icon>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{
          vertical: 0,
          horizontal: 0,
        }}
      >
        <Box p={2}>
          <FlexRow alignItems="flex-start">
            <Box>
              <Font variant={'h5'} color="title" display="block">
                <Text tId={action.type} />
              </Font>
              <Font variant={'body2'} color="textSecondary" display="block">
                {formatDateTimeReadable(action.createdAt)}
              </Font>
            </Box>
            <OrderActionStatusTag type={action.type} status={action.status} />
          </FlexRow>
          <Box pt={2} width={'100%'}>
            {action.description && <Font variant={'body2'}>{action.description}</Font>}
          </Box>
          <CustomPaper fill="grey.light" style={{ padding: '16px' }}>
            {action.orderLines.map((orderLine) => (
              <FlexRow>
                <Box>
                  <Font variant={'body2'} display="block">
                    {orderLine.name}
                  </Font>
                </Box>
                <Box ml={2}>
                  <Font variant={'body2'} display="block">
                    {formatMoney(orderLine.amount)}
                  </Font>
                </Box>
              </FlexRow>
            ))}
            <CustomDivider dashed margin={2} />
            <FlexRow>
              <Font variant={'body2'} display="block">
                <Text strong tId={'TOTAL'} />
              </Font>
              <Font variant={'body2'} display="block">
                <strong>{formatMoney(action.amount)}</strong>
              </Font>
            </FlexRow>
          </CustomPaper>
          <Box pt={2}>
            <Font variant={'caption'} color={'textSecondary'}>
              ID <Text tId={action.type} />: {action.id}
            </Font>
          </Box>
        </Box>
      </Menu>
    </>
  );
};

interface OrderActionStatusTagProps {
  type: OrderActionType;
  status: CaptureStatus | RefundStatus | PayoutStatus | OrderStatus;
}

const OrderActionStatusTag = ({ type, status }: OrderActionStatusTagProps) => {
  switch (type) {
    case OrderActionType.capture:
      return <CaptureStatusTag status={status as CaptureStatus} />;
    case OrderActionType.refund:
      return <RefundStatusTag status={status as RefundStatus} />;
    case OrderActionType.payout:
      return <PayoutStatusTag status={status as PayoutStatus} />;
    default:
      return null;
  }
};
